import { Link } from 'react-router-dom';
import { MapPin, Eye, MessageSquare, Layers, Leaf, ExternalLink, BadgeCheck, ShieldCheck } from 'lucide-react';
import FavoriteButton from './FavoriteButton';
import { formatListingLocation } from '../utils/location';

const conditionColors = {
  excellent: 'text-emerald-400',
  good: 'text-blue-400',
  fair: 'text-amber-400',
  salvage: 'text-red-400'
};

const EXTERNAL_SOURCE_LABELS = {
  gumtree: 'Gumtree',
  fb_marketplace: 'Facebook Marketplace',
  craigslist: 'Craigslist',
};

export default function ListingCard({ listing }) {
  const formatPrice = (price) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(price);
  };

  return (
    <Link 
      to={`/listing/${listing.listing_id}`}
      className="listing-card group relative overflow-hidden rounded-lg bg-card/50 border border-border/20 hover:border-accent/30 transition-all duration-500 card-hover"
      data-testid={`listing-card-${listing.listing_id}`}
    >
      {/* Image */}
      <div className="aspect-[4/3] overflow-hidden relative">
        <FavoriteButton listingId={listing.listing_id} variant="card" />
        {listing.images?.[0] ? (
          <img 
            src={listing.images[0]} 
            alt={listing.title}
            className="w-full h-full object-cover listing-card-image"
          />
        ) : (
          <div className="w-full h-full bg-secondary flex items-center justify-center">
            <Layers className="w-12 h-12 text-muted-foreground/30" />
          </div>
        )}
        <div className="image-overlay absolute inset-0" />
        
        {/* Badges */}
        <div className="absolute top-3 left-3 right-3 flex flex-wrap items-center gap-1.5">
          <span className={`px-2 py-1 text-xs font-medium rounded-full badge-${listing.availability}`}>
            {listing.availability === 'available' ? 'Available' : listing.availability}
          </span>
          {listing.has_digital_model && (
            <span className="px-2 py-1 text-xs font-medium rounded-full bg-primary/20 text-primary border border-primary/30">
              3D Model
            </span>
          )}
          {listing.carbon?.kg_co2e_total >= 1 && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-emerald-500/15 text-emerald-300 border border-emerald-500/30"
              title={`Avoids ~${Math.round(listing.carbon.kg_co2e_total)} kg CO₂e vs. buying new (${listing.carbon.method === 'dims' ? 'dimensional estimate' : 'category baseline'} · Bath ICE v3.0)`}
              data-testid={`carbon-badge-${listing.listing_id}`}
            >
              <Leaf className="w-3 h-3" />
              {listing.carbon.kg_co2e_total >= 1000
                ? `${(listing.carbon.kg_co2e_total / 1000).toFixed(1)} t CO₂e saved`
                : `${Math.round(listing.carbon.kg_co2e_total)} kg CO₂e saved`}
            </span>
          )}
          {listing.external_source && (
            <span
              className="inline-flex items-center gap-1 px-2 py-1 text-xs font-medium rounded-full bg-blue-500/15 text-blue-300 border border-blue-500/30"
              title={`Listed on ${EXTERNAL_SOURCE_LABELS[listing.external_source] || listing.external_source}`}
              data-testid={`external-badge-${listing.listing_id}`}
            >
              <ExternalLink className="w-3 h-3" />
              {EXTERNAL_SOURCE_LABELS[listing.external_source] || listing.external_source}
            </span>
          )}
        </div>

        {/* Price */}
        <div className="absolute bottom-3 right-3">
          <span className="font-playfair text-xl font-bold text-gold-gradient">
            {formatPrice(listing.price)}
          </span>
          <span className="text-xs text-muted-foreground ml-1">/{listing.unit}</span>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-playfair text-lg font-semibold text-foreground mb-1 line-clamp-1 group-hover:text-accent transition-colors">
          {listing.title}
        </h3>
        
        <div className="flex items-center gap-2 mb-3">
          <span className={`text-xs font-medium capitalize ${conditionColors[listing.condition]}`}>
            {listing.condition}
          </span>
          <span className="text-muted-foreground/30">•</span>
          <span className="text-xs text-muted-foreground capitalize">{listing.category}</span>
          {listing.is_passport_quality && (
            <span
              className="inline-flex items-center gap-1 text-[10px] text-emerald-300 bg-emerald-500/10 border border-emerald-500/30 px-1.5 py-0.5 rounded-full"
              title="Passport quality — carbon stats + salvage origin documented. Accepted as Green Star Fitouts / Climate Active / NABERS evidence."
              data-testid={`passport-quality-badge-${listing.listing_id}`}
            >
              <ShieldCheck className="w-3 h-3" />
              <span className="hidden sm:inline">Passport</span>
            </span>
          )}
          {(listing.mci ?? 0) > 0 && (
            <span
              className="inline-flex items-center gap-1 text-[10px] text-emerald-300 bg-emerald-500/5 border border-emerald-500/20 px-1.5 py-0.5 rounded-full font-mono"
              title={`Material Circularity Indicator (MCI): ${listing.mci.toFixed(2)} / 1.00 — Ellen MacArthur Foundation method, ISO 59020:2024 aligned. New products typically score 0.10–0.30; reclaimed items score 0.70+.`}
              data-testid={`mci-badge-${listing.listing_id}`}
            >
              MCI {listing.mci.toFixed(2)}
            </span>
          )}
          {listing.is_verified_seller && (
            <span className="ml-auto inline-flex items-center gap-1 text-[10px] text-accent" title="PatinaMart-verified seller">
              <BadgeCheck className="w-3 h-3" />
              <span className="hidden sm:inline">Verified</span>
            </span>
          )}
        </div>

        <div className="flex items-center justify-between text-xs text-muted-foreground">
          <div className="flex items-center gap-1">
            <MapPin className="w-3 h-3" />
            <span className="line-clamp-1">{formatListingLocation(listing)}</span>
            {typeof listing.distance_km === 'number' && (
              <span className="ml-1 px-1.5 py-0.5 rounded bg-accent/15 text-accent text-[10px] font-medium" data-testid={`distance-${listing.listing_id}`}>
                {listing.distance_km < 1 ? '<1' : Math.round(listing.distance_km)} km
              </span>
            )}
          </div>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-1">
              <Eye className="w-3 h-3" />
              <span>{listing.views || 0}</span>
            </div>
            <div className="flex items-center gap-1">
              <MessageSquare className="w-3 h-3" />
              <span>{listing.inquiries || 0}</span>
            </div>
          </div>
        </div>
      </div>
    </Link>
  );
}
